import React, { useState, useRef, useEffect } from "react";
import { withTranslation } from "react-i18next";
import { connect } from "react-redux";
import i18next from "i18next";
import * as moment from "moment";
import axios from "axios";
import { handleApiError } from "@utils/errorHandler";
import { getRedirectUrl } from "@utils/authService";
import { languageFlagPathArray } from "@utils/constants";
import adminLogo from "@icons/ic-hesus-logo.png";
import defaultFlag from "@icons/flags/ic-default-flag.png";
import "./Login.scss";

const Login = ({ t }) => {
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showLangDropdown, setShowLangDropdown] = useState(false);
  const [selectedLang, setSelectedLang] = useState({
    id: "1",
    lang: "en",
    flag: languageFlagPathArray[1],
    title: "UK",
  });

  const [emailError, setEmailError] = useState(false);
  const [passwordError, setPasswordError] = useState(false);
  const formRef = useRef(null);
  const langDropdownRef = useRef(null);
  const [languages, setLanguages] = useState([]);

  useEffect(() => {
    axios
      .get(`/language_list`, {
        headers: { Accept: "application/json" },
      })
      .then((response) => {
        const langArray = [];
        response.data?.map((langInfo) => {
          langArray.push({
            id: langInfo.id,
            lang: langInfo.locale,
            flag: languageFlagPathArray[langInfo.id]
              ? languageFlagPathArray[langInfo.id]
              : defaultFlag,
            title: langInfo.name,
            dateFormat: langInfo.dateFormat,
          });
        });
        setLanguages([...langArray]);
        localStorage.setItem("language_list", JSON.stringify([...langArray]));
      })
      .catch((error) => handleApiError(error));
  }, []);
  // Initialize language from localStorage or browser
  useEffect(() => {
    if (languages?.length > 0) {
      const locale = localStorage.getItem("locale") || i18next.language || "en";
      const langItem = languages.find((l) => l.lang === locale) || languages[0];
      setSelectedLang(langItem);
      moment.locale(locale);
      i18next.changeLanguage(locale);
    }
  }, [languages]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        langDropdownRef.current &&
        !langDropdownRef.current.contains(event.target)
      ) {
        setShowLangDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Clear previous errors
    setError("");
    setEmailError(false);
    setPasswordError(false);

    const email = e.target._email.value.trim();
    const password = e.target._password.value;

    // Validate fields
    let hasError = false;
    if (!email) {
      setEmailError(true);
      hasError = true;
    }
    if (!password) {
      setPasswordError(true);
      hasError = true;
    }
    if (hasError) {
      return;
    }

    setIsLoading(true);

    try {
      const response = await axios.post("/api/v1/auth/login", {
        email,
        password,
      });

      if (response.data.access_token) {
        // Store tokens in localStorage
        localStorage.setItem("access_token", response.data.access_token);
        localStorage.setItem("refresh_token", response.data.refresh_token);
        localStorage.setItem("token_expires_in", response.data.expires_in);
        localStorage.setItem("user_info", JSON.stringify(response.data.user));

        // After successful login:
        const redirectUrl = getRedirectUrl(); // Returns saved URL or "/" as default
        window.location.href = redirectUrl;
      }
    } catch (err) {
      console.error("Login error:", err);
      if (err.response?.status === 401) {
        setError(t("LOGIN_PAGE_ERROR_INVALID_CREDENTIALS"));
      } else if (err.response?.status === 403) {
        setError(t("LOGIN_PAGE_ERROR_ACCOUNT_INACTIVE"));
      } else {
        setError(t("LOGIN_PAGE_ERROR_GENERIC"));
      }
      setIsLoading(false);
    } finally {
      setIsLoading(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleLanguageChange = (langItem) => {
    setSelectedLang(langItem);
    setShowLangDropdown(false);
    moment.locale(langItem.lang);
    localStorage.setItem("locale", langItem.lang);
    i18next.changeLanguage(langItem.lang);

    // Notify server about language change
    axios
      .get(`/change_language/${langItem.id}`, {
        headers: { Accept: "application/json" },
      })
      .catch((error) => handleApiError(error));
  };

  return (
    <div className="login-page">
      <div className="login-container">
        <div className="login-left">
          <div className="login-content">
            <div className="login-header">
              <div className="admin-logo">
                <img src={adminLogo} alt="Hesus Admin" />
              </div>
              <div className="login-language-selector" ref={langDropdownRef}>
                <button
                  type="button"
                  className="lang-dropdown-toggle"
                  onClick={() => setShowLangDropdown(!showLangDropdown)}
                >
                  <img
                    src={selectedLang?.flag}
                    alt={selectedLang?.title}
                    className="lang-flag"
                    title={selectedLang.title}
                  />
                </button>
                {showLangDropdown && (
                  <div className="lang-dropdown-menu">
                    {languages.length > 0 &&
                      languages.map((langItem) => (
                        <div
                          key={langItem.id}
                          className={`lang-dropdown-item ${
                            langItem.id === selectedLang?.id ? "active" : ""
                          }`}
                          onClick={() => handleLanguageChange(langItem)}
                        >
                          <img
                            src={langItem.flag}
                            alt={langItem.title}
                            className="lang-flag"
                            title={langItem.title}
                          />
                        </div>
                      ))}
                  </div>
                )}
              </div>
            </div>

            <div className="login-form-container">
              <h1 className="login-title">{t("LOGIN_PAGE_TITLE")}</h1>

              {error && <div className="login-error">{error}</div>}

              <form
                ref={formRef}
                onSubmit={handleSubmit}
                className="login-form"
              >
                <div className="form-group">
                  <label
                    htmlFor="email"
                    className={`form-label ${emailError ? "label-error" : ""}`}
                  >
                    {t("LOGIN_PAGE_LOGIN_LABEL")}*
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="_email"
                    className={`form-input ${emailError ? "input-error" : ""}`}
                    placeholder={t("LOGIN_PAGE_LOGIN_PLACEHOLDER")}
                    onChange={(e) => e.target.value && setEmailError(false)}
                  />
                </div>

                <div className="form-group">
                  <label
                    htmlFor="password"
                    className={`form-label ${
                      passwordError ? "label-error" : ""
                    }`}
                  >
                    {t("LOGIN_PAGE_PASSWORD_LABEL")}*
                  </label>
                  <div className="password-input-wrapper">
                    <input
                      type={showPassword ? "text" : "password"}
                      id="password"
                      name="_password"
                      className={`form-input ${
                        passwordError ? "input-error" : ""
                      }`}
                      placeholder={t("LOGIN_PAGE_PASSWORD_PLACEHOLDER")}
                      onChange={(e) =>
                        e.target.value && setPasswordError(false)
                      }
                    />
                    <button
                      type="button"
                      className="password-toggle"
                      onClick={togglePasswordVisibility}
                      aria-label={
                        showPassword ? "Hide password" : "Show password"
                      }
                    >
                      <i
                        className={
                          showPassword ? "fa fa-eye-slash" : "fa fa-eye"
                        }
                      />
                    </button>
                  </div>
                </div>

                <button
                  type="submit"
                  className="login-button"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <span className="loading-spinner" />
                  ) : (
                    t("LOGIN_PAGE_LOGIN_ACTION")
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>

        <div className="login-right">
          <div className="login-bg-overlay">
            <div className="hesus-logo-container">
              <svg
                className="hesus-hexagon"
                viewBox="0 0 200 200"
                xmlns="http://www.w3.org/2000/svg"
              ></svg>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({});

const mapDispatchToProps = {};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withTranslation()(Login));
